import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Bell as BellIcon, Check } from 'lucide-react'
import { withIcon3D } from '@/components/icons3d/Icon3D'

const Bell = withIcon3D('bell', BellIcon)
import { useSelector, useDispatch } from 'react-redux'
import { markAllAsReadAsync } from '@/features/notifications/notificationSlice'
import NotificationList from './NotificationList'
import { assistAnchor } from '@/features/assist/anchors'

/**
 * Notification Badge Component
 *
 * Header badge showing unread notification count.
 * Clicking opens notification panel dropdown.
 *
 * @component
 */
const NotificationBadge = () => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)

  const { unreadCount } = useSelector((state) => state.notification)

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsReadAsync())
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* Topbar icon buttons are one size (F-7 addendum): the `icon` size
            variant is already 40px, with the responsive min-h/min-w-11
            bump for touch baked into buttonVariants' base classes. */}
        <Button variant="ghost" size="icon" className="relative shrink-0" aria-label="Notifications" {...assistAnchor('nav.notifications')}>
          <Bell className="size-5" aria-hidden="true" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute right-0 top-0 flex h-5 min-w-5 items-center justify-center px-1 text-[10px] tabular-nums"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="flex w-96 max-h-[var(--radix-popover-content-available-height)] flex-col overflow-hidden p-0" align="end" sideOffset={8} collisionPadding={12} aria-label="Notifications">
        {/* Header */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
          <h3 className="text-body-lg font-semibold text-ink">
            Notifications
          </h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11"
              onClick={handleMarkAllAsRead}
            >
              <Check className="size-4" aria-hidden="true" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notification List */}
        <NotificationList onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}

export default NotificationBadge
